import { Text as ChakraText } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { getFieldsMapping, getFilterText } from '../../../utils/mediaLibrary'
import type { CustomMetadataResponse } from '../../interfaces'
import type {
  FilterValidationObject,
  FormValidationObject,
  SearchQueryJson,
} from '../../interfaces/MediaLibrary'

const TextWrapper = ({
  text,
  textColor,
}: {
  text: string
  textColor: string
}): React.ReactElement => (
  <ChakraText color={textColor} fontWeight="medium" isTruncated>
    {text}
  </ChakraText>
)

interface Props {
  searchQueryJson: SearchQueryJson
  validationObject: FormValidationObject
  customMetadataFieldsMapping: { [fieldName: string]: CustomMetadataResponse }
  textColor: string
  groupId: number
  filterId: number
}

const FilterText = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    customMetadataFieldsMapping,
    textColor,
    groupId,
    filterId,
  } = props

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId].expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const fieldLabel = useMemo(
    () =>
      getFieldsMapping({
        fieldName: expression.fieldName,
        customMetadataField: customMetadataFieldsMapping[expression.fieldName],
      }).displayText,
    [searchQueryJson, groupId, filterId, customMetadataFieldsMapping],
  )

  const filterValidationObject: FilterValidationObject = useMemo(
    () => ({
      isValid: !validationObject.errors[groupId][filterId],
      error: validationObject.errors[groupId][filterId],
    }),
    [validationObject, groupId, filterId],
  )

  const filterText = useMemo(
    () => getFilterText({ expression }),
    [searchQueryJson, groupId, filterId],
  )

  if (
    typeof expression.value === 'undefined' ||
    expression.value === null ||
    (typeof expression.value === 'string' && expression.value.trim() === '') ||
    (Array.isArray(expression.value) && expression.value.length === 0)
  ) {
    return (
      <TextWrapper text={`${fieldLabel} has no value`} textColor={textColor} />
    )
  }

  if (!filterValidationObject.isValid) {
    return (
      <TextWrapper
        text={`${fieldLabel} has an invalid value`}
        textColor={textColor}
      />
    )
  }

  if (!filterText) {
    return (
      <TextWrapper
        text={`${fieldLabel} has an incomplete value`}
        textColor={textColor}
      />
    )
  }

  return ['private', 'transparency', 'published'].includes(expression.fieldName) ? (
    <TextWrapper text={`${filterText}`} textColor={textColor} />
  ) : (
    <TextWrapper text={`${fieldLabel}${filterText}`} textColor={textColor} />
  )
}

export default FilterText
